import React, { useState } from 'react';
import Form from '@dashboard/components/Formik/Form';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsContainer from '@/dashboard/components/SettingsContainer';

import Link from '@dashboard/components/Link';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { TextInput } from '@dashboard/components/Formik';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import TabLink from '@dashboard/components/TabLink';
import { rootDomain } from '@dashboard/services/env';
import { InlineSnippet } from '@dashboard/components/InlineSnippet';
import Spinner from '@dashboard/components/Spinner/Spinner';
import * as Yup from 'yup';
import Callout from '@dashboard/components/Callout';
import Features from '@dashboard/enums/features';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import { isEmpty } from 'lodash';
import { getSellerSubdomain } from '@dashboard/services/getSellerDomain';
import { domainStatusKey, pagesKey } from '@dashboard/lib/queryKeys';
import SettingsDescription from '@dashboard/components/SettingsDescription';

function getHomepageLabel(homepage) {
  if (homepage.type === 'customerPortal') return 'Customer portal';
  if (homepage.type === 'checkout') return 'Checkout';
  if (homepage.type === 'redirect') return 'Redirect to URL';
  return 'No homepage (blank)';
}

const SettingsDomains = () => {
  const { seller, setSeller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const queryClient = useQueryClient();

  return (
    <>
      <Card variant="root">
        <CardHeader variant="root">
          <div className="flex flex-col max-w-lg">
            <CardHeaderTitle variant="root" id="subdomain">
              Domain settings
            </CardHeaderTitle>
            <SettingsDescription className="mt-2">
              Host your pages and customer portal on your custom domain or on your Checkout Page
              subdomain.
            </SettingsDescription>
          </div>
        </CardHeader>

        <CardContent variant="root" className="pb-8">
          <SettingsContainer size="lg">
            <Callout variant="info">
              <Callout.Heading>Note</Callout.Heading>
              <Callout.Content>
                If you change your URL, your old URL will no longer work, and you will need to
                update your links.
              </Callout.Content>
            </Callout>

            {!isEmpty(seller) && (
              <>
                <Form
                  formSubmitAlign="end"
                  initialValues={{
                    name: seller.name,
                  }}
                  onSubmit={async (values, actions) => {
                    const { data, error } = await updateSeller(values);

                    if (data) {
                      actions.resetForm({
                        values: {
                          name: data.name,
                        },
                      });
                    }

                    return {
                      data,
                      error,
                      onSuccess: () => queryClient.invalidateQueries({ queryKey: pagesKey }),
                    };
                  }}
                  validationSchema={Yup.object().shape({
                    name: Yup.string().required('Name is required').min(3).max(50),
                  })}
                  fields={[
                    {
                      name: 'name',
                      required: true,
                      type: 'custom',
                      renderField: field => (
                        <div className="flex w-full">
                          <TextInput {...field} required className="rounded-r-none w-full" />
                          <div className="bg-gray-50 w-2/3 flex items-center rounded-r-md border border-gray-200 border-l-0 appearance-none py-2 px-2 text-gray-800">
                            .{rootDomain}
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />

                <div>
                  <div>
                    Checkout Page homepage:{' '}
                    <Link href={getSellerSubdomain(seller)} external target="_blank">
                      {getHomepageLabel(seller.homepage)}
                    </Link>
                  </div>
                  <div>
                    <Link variant="secondary" to="/settings/general#homepage" className="text-sm">
                      Change homepage
                    </Link>
                  </div>
                </div>
              </>
            )}
          </SettingsContainer>

          <SettingsContainer size="lg">
            <SettingsDivider />
          </SettingsContainer>

          <SettingsContainer size="lg">
            <CanUseFeature feature={Features.CUSTOM_DOMAIN}>
              <SettingsHeader id="custom-domain">Custom domain</SettingsHeader>

              <SettingsDescription className="mb-4">
                Add your own (sub)domain to your pages and customer portal.
              </SettingsDescription>

              <Callout variant="info">
                <Callout.Heading>Note</Callout.Heading>
                <Callout.Content>
                  If your domain is <InlineSnippet>example.com</InlineSnippet>, we recommend using{' '}
                  <InlineSnippet>buy.example.com</InlineSnippet> as your custom subdomain. You can
                  replace <InlineSnippet>buy</InlineSnippet> with anything you like.
                </Callout.Content>
              </Callout>

              {!isEmpty(seller) && (
                <>
                  <Form
                    formSubmitAlign="end"
                    initialValues={{
                      customDomain: seller.customDomain,
                    }}
                    onSubmit={async (values, actions) => {
                      const { data, error } = await updateSeller(values);

                      if (data) {
                        actions.resetForm({
                          values: {
                            customDomain: data.customDomain,
                          },
                        });
                      }

                      return {
                        data,
                        error,
                        onSuccess: () => {
                          setSeller(data);
                          queryClient.invalidateQueries({ queryKey: pagesKey });
                        },
                      };
                    }}
                    fields={[
                      {
                        name: 'customDomain',
                        type: 'text',
                        label: 'Custom domain',
                        placeholder: 'buy.example.com',
                      },
                    ]}
                  />

                  <DomainConfiguration domain={seller.customDomain} />

                  {seller.customDomain && (
                    <div className="mt-4">
                      <div>
                        Custom domain homepage:{' '}
                        <Link href={getSellerSubdomain(seller)} external target="_blank">
                          {getHomepageLabel(seller.homepage)}
                        </Link>
                      </div>

                      <div>
                        <Link
                          variant="secondary"
                          to="/settings/general#homepage"
                          className="text-sm"
                        >
                          Change homepage
                        </Link>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CanUseFeature>
          </SettingsContainer>
        </CardContent>
      </Card>
    </>
  );
};

function useDomainStatus({ domain }) {
  const { token, sellerId } = useAuth();

  const { data, isRefetching, isLoading, refetch } = useQuery({
    queryKey: domainStatusKey(domain),
    queryFn: () => fetcher(`/domains/${domain}/verify?sellerId=${sellerId}`, token),
    enabled: !!domain,
    retryOnMount: true,
    refetchInterval: 5000,
  });

  return {
    status: data?.status,
    domainJson: data?.domain,
    refetch,
    isLoading: isRefetching || isLoading,
  };
}

export const getSubdomain = (name, apexName) => {
  if (name === apexName) return null;
  return name.slice(0, name.length - apexName.length - 1);
};

function DomainConfiguration({ domain }) {
  const [recordType, setRecordType] = useState('CNAME');

  const { status, domainJson, isLoading } = useDomainStatus({
    domain,
  });

  if (!domain) return null;

  if (!status || !domainJson) {
    if (isLoading) {
      return (
        <div className="flex justify-end items-center text-gray-500 mt-4">
          <Spinner size="small" />
          <span className="ml-2">Checking domain status</span>
        </div>
      );
    }

    return null;
  }

  const subdomain = getSubdomain(domainJson.name, domainJson.apexName);

  const txtVerification =
    (status === 'Pending Verification' && domainJson.verification?.find(x => x.type === 'TXT')) ||
    null;

  return (
    <div className="mt-4">
      {status !== 'Valid Configuration' && <h3>Create DNS records</h3>}

      {txtVerification ? (
        <div className="mb-4 text-left">
          <p className="mb-4">
            Add the following TXT record on <InlineSnippet>{domainJson.apexName}</InlineSnippet> to
            prove ownership of <InlineSnippet>{domainJson.name}</InlineSnippet>:
          </p>

          <div className="flex items-start justify-start space-x-10 rounded-md bg-gray-100 p-2">
            <div>
              <p className="text-sm font-bold">Type</p>
              <p className="mt-2 font-mono text-sm">{txtVerification.type}</p>
            </div>
            <div>
              <p className="text-sm font-bold">Name</p>
              <p className="mt-2 font-mono text-sm">
                {txtVerification.domain.slice(
                  0,
                  txtVerification.domain.length - domainJson.apexName.length - 1,
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-bold">Value</p>
              <p className="mt-2 font-mono text-sm">
                <span className="text-ellipsis wrap-anywhere">{txtVerification.value}</span>
              </p>
            </div>
          </div>

          <p className="mt-4 text-sm">
            Warning: if you are using this domain for another site, adding this TXT record will
            transfer domain ownership away from that site and break it. Exercise caution when
            setting this record.
          </p>
        </div>
      ) : status === 'Unknown Error' ? (
        <p>{domainJson.error.message}</p>
      ) : null}

      {status === 'Pending Verification' && (
        <>
          <div className="flex justify-start space-x-4">
            {subdomain ? (
              <>
                <button type="button" onClick={() => setRecordType('CNAME')}>
                  <TabLink selected={recordType === 'CNAME'}>CNAME Record (recommended)</TabLink>
                </button>

                <button type="button" onClick={() => setRecordType('A')}>
                  <TabLink selected={recordType === 'A'}>A Record</TabLink>
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setRecordType('A')}>
                  <TabLink selected={recordType === 'A'}>A Record (recommended)</TabLink>
                </button>

                <button type="button" onClick={() => setRecordType('CNAME')}>
                  <TabLink selected={recordType === 'CNAME'}>CNAME Record</TabLink>
                </button>
              </>
            )}
          </div>

          <div className="my-4 text-left">
            <p className="my-4">
              To configure your {recordType === 'A' ? 'apex domain' : 'subdomain'} (
              <InlineSnippet>
                {recordType === 'A' ? domainJson.apexName : domainJson.name}
              </InlineSnippet>
              ), add the following {recordType} record on your DNS provider to continue:
            </p>
            <div className="flex items-start justify-start space-x-10 rounded bg-gray-100 p-2">
              <div>
                <p className="text-sm font-bold">Type</p>
                <p className="mt-2 font-mono text-sm">{recordType}</p>
              </div>
              <div>
                <p className="text-sm font-bold">Name</p>
                <p className="mt-2 font-mono text-sm">
                  {recordType === 'A' ? '@' : (subdomain ?? 'www')}
                </p>
              </div>
              <div>
                <p className="text-sm font-bold">Value</p>
                <p className="mt-2 font-mono text-sm wrap-anywhere">
                  {recordType === 'A' ? `76.76.21.21` : `cname.${rootDomain}`}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="mb-4">
        {status === 'Pending Verification' ? (
          <Callout variant="warning">
            <Callout.Heading>{status}</Callout.Heading>
            <Callout.Content>
              If you just configured your domain, it may take some time until we can verify it. We
              will check your domain status every 5 seconds.
            </Callout.Content>
          </Callout>
        ) : status === 'Valid Configuration' ? (
          <Callout variant="success">
            <Callout.Heading>{status}</Callout.Heading>
            <Callout.Content>
              If you just configured your domain, it may take up to an hour until your new checkout
              links work.
            </Callout.Content>
          </Callout>
        ) : (
          <Callout variant="error">
            <Callout.Heading>{status}</Callout.Heading>
          </Callout>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-end items-center text-gray-500 mt-4">
          <Spinner size="small" />
          <span className="ml-2">Checking domain status</span>
        </div>
      )}
    </div>
  );
}

export default SettingsDomains;
